/**
 * AI 智能分类 · 异步归档任务切片
 *
 * 用户在对话里点"创建"后，photobook 接 rule 立即返回 folderID（建夹同步），
 * 但**照片归档是 photobook 内部异步**——根据 rule 后台搬运可能要分钟级。
 * 本 slice 维护"任务可追溯"列表：让用户随时回看每个新建相册的归档进度。
 *
 * 状态机
 *   archiving → completed | timeout
 *
 * 完成判定（轮询 /web/folder/statistic.photoCount）：
 *   · photoCount ≥ expectedCount × 0.9 → completed（命中接近预期）
 *   · 创建后 10 分钟仍未达标 → timeout（防止永久占着轮询资源）
 *
 * 持久化：通过 taskPersistence.ts 写 localStorage（key=ai_classify_tasks_v1）。
 * 刷新页面 / 重开标签页都能看到任务列表。
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { conversationCleared, teamSwitched } from './sessionSlice'

export type ArchiveTaskStatus = 'archiving' | 'completed' | 'timeout'

export interface ArchiveTask {
  /** 客户端生成 · `task-{ts}-{rand}` 用作唯一 key */
  taskId: string
  /** photobook 返回的真实 folderID */
  folderID: number
  /** 用户给的相册名 */
  folderName: string
  /** LLM estimatedCount · 用作完成阈值（实际 ≥ 90% 即视作完成） */
  expectedCount: number
  /** 轮询拿到的实际 photoCount · 进度展示用 */
  currentCount: number
  status: ArchiveTaskStatus
  /** 毫秒时间戳 · 超时判断 + 列表排序 */
  createdAt: number
  /** completed / timeout 时填写 */
  completedAt?: number
}

interface TaskState {
  tasks: ArchiveTask[]
  /**
   * 有任务从 archiving 转到 completed 触发；用户点开任务列表后清除。
   * UI：抽屉头部"分类任务"按钮的红点闪烁开关。
   */
  hasUnreadCompletion: boolean
  /**
   * 待弹 toast 通知的 taskId 队列 · 持久化跨刷新
   *
   * 设计：
   *  · 任务从 archiving 转 completed 时入队（taskCompleted reducer 内）
   *  · ArchiveTaskCompletionToasts 组件订阅，每弹一条就 dispatch notificationShown 出队
   *  · 持久化让"任务在用户切到详情页 / 关页面时完成"也能在回来时补发通知
   *  · 不在持久化里就重弹的旧情况彻底消除（v4.6 之前的 seenRef 局限）
   */
  pendingNotifications: string[]
}

const initialState: TaskState = {
  tasks: [],
  hasUnreadCompletion: false,
  pendingNotifications: []
}

const taskSlice = createSlice({
  name: 'aiClassify/task',
  initialState,
  reducers: {
    /** 建夹成功后立即创建一条 archiving 任务 */
    taskCreated(state, action: PayloadAction<ArchiveTask>) {
      // 同 folderID 的旧记录视作过时，覆盖（如用户重复跑同方案）
      const idx = state.tasks.findIndex(
        (t) => t.folderID === action.payload.folderID
      )
      if (idx >= 0) state.tasks[idx] = action.payload
      else state.tasks.unshift(action.payload)
    },

    /** 轮询拿到的中间态 photoCount · 不改 status，仅刷新进度 */
    taskProgressUpdated(
      state,
      action: PayloadAction<{ taskId: string; currentCount: number }>
    ) {
      const t = state.tasks.find((x) => x.taskId === action.payload.taskId)
      if (!t || t.status !== 'archiving') return
      t.currentCount = action.payload.currentCount
    },

    /** photoCount 达到完成阈值 · 触发红点闪烁 + 入队待提示 toast */
    taskCompleted(
      state,
      action: PayloadAction<{
        taskId: string
        currentCount: number
        completedAt: number
      }>
    ) {
      const t = state.tasks.find((x) => x.taskId === action.payload.taskId)
      if (!t || t.status !== 'archiving') return
      t.status = 'completed'
      t.currentCount = action.payload.currentCount
      t.completedAt = action.payload.completedAt
      state.hasUnreadCompletion = true
      // 入队待弹通知（去重 · 防止极端情况重复入队）
      if (!state.pendingNotifications.includes(action.payload.taskId)) {
        state.pendingNotifications.push(action.payload.taskId)
      }
    },

    /** 创建后 10 分钟仍未达标 · 停止轮询，但保留在列表里 */
    taskTimedOut(state, action: PayloadAction<{ taskId: string }>) {
      const t = state.tasks.find((x) => x.taskId === action.payload.taskId)
      if (!t || t.status !== 'archiving') return
      t.status = 'timeout'
      t.completedAt = Date.now()
    },

    /** 用户点开任务列表后调 · 清红点闪烁标志 */
    unreadCompletionCleared(state) {
      state.hasUnreadCompletion = false
    },

    /** 启动时从 localStorage 恢复 · pendingNotifications 也一起恢复 */
    tasksHydrated(state, action: PayloadAction<TaskState>) {
      state.tasks = action.payload.tasks
      state.hasUnreadCompletion = action.payload.hasUnreadCompletion
      state.pendingNotifications = action.payload.pendingNotifications ?? []
    },

    /** Toast 已弹出 · 把这条 taskId 从待提示队列里移除 */
    notificationShown(state, action: PayloadAction<{ taskId: string }>) {
      state.pendingNotifications = state.pendingNotifications.filter(
        (id) => id !== action.payload.taskId
      )
    },

    /** 用户在列表里手动删除某条任务（已完成或超时） */
    taskRemoved(state, action: PayloadAction<{ taskId: string }>) {
      state.tasks = state.tasks.filter(
        (t) => t.taskId !== action.payload.taskId
      )
    }
  },
  extraReducers: (builder) => {
    // 任务列表跨会话存在 (本体是相册的归档进度), 不跟随会话切换清空。
    // 切团队 (teamSwitched) 也保留, 任务归属是相册维度而非会话维度。
    // 显式 noop 让审阅时一目了然: 三种重置 action 都不影响 task 子树
    builder
      .addCase(conversationCleared, (state) => state)
      .addCase(teamSwitched, (state) => state)
  }
})

export const {
  taskCreated,
  taskProgressUpdated,
  taskCompleted,
  taskTimedOut,
  unreadCompletionCleared,
  tasksHydrated,
  notificationShown,
  taskRemoved
} = taskSlice.actions

export default taskSlice.reducer

/** 给 taskPersistence 序列化用 */
export function selectTaskStateForPersistence(state: TaskState): TaskState {
  return state
}
